import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

// Get token from localStorage
const token = localStorage.getItem('token');

export const getPostgreSQLCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  console.log('🔍 Frontend: getPostgreSQLCredentialsAndCreateToken() called:');
  console.log('   - Connector ID:', connectorId);
  console.log('   - Master Connector ID:', mcId);
  console.log('   - Master Connectors count:', masterConnectors?.length);

  try {
    // Find the PostgreSQL master connector details
    const postgresConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === mcId?.toString() ||
       mc.mcId?.toString() === mcId?.toString()) &&
      mc.mcName === "PostgreSQL Database"
    );

    if (!postgresConnector) {
      throw new Error('PostgreSQL Database master connector not found');
    }

    console.log('   - Fetching connector parameters from API...');
    // Fetch connector parameters from the API endpoint using connector instance ID (ctd_id)
    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });

    if (!paramResponse.ok) {
      throw new Error('Failed to fetch connector parameters');
    }

    const connectorParams = await paramResponse.json();
    console.log('   - Connector parameters fetched successfully');

    // Extract database credentials from parameters
    const dbHost = connectorParams.find(param => param.scpName === "db_host")?.scpValue;
    const dbPort = connectorParams.find(param => param.scpName === "db_port")?.scpValue;
    const dbName = connectorParams.find(param => param.scpName === "db_name")?.scpValue;
    const dbUsername = connectorParams.find(param => param.scpName === "db_username")?.scpValue;
    const dbPassword = connectorParams.find(param => param.scpName === "db_password")?.scpValue;

    // Validate all required parameters are present
    if (!dbHost || !dbPort || !dbName || !dbUsername || !dbPassword) {
      const missingParams = [];
      if (!dbHost) missingParams.push('db_host');
      if (!dbPort) missingParams.push('db_port');
      if (!dbName) missingParams.push('db_name');
      if (!dbUsername) missingParams.push('db_username');
      if (!dbPassword) missingParams.push('db_password');
      
      throw new Error(`Missing required database parameters: ${missingParams.join(', ')}. Please configure them in Parameters settings.`);
    }

    console.log('   - Database credentials extracted');
    console.log('   - Host:', dbHost);
    console.log('   - Port:', dbPort);
    console.log('   - Database:', dbName);
    console.log('   - Username:', dbUsername);

    // First, test the connection
    console.log('   - Testing database connection...');
    const testResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/database-connector/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        host: dbHost,
        port: dbPort,
        databaseName: dbName, // Backend expects 'databaseName', not 'dbName'
        username: dbUsername,
        password: dbPassword
      })
    });

    if (!testResponse.ok) {
      const errorData = await testResponse.json().catch(() => ({ message: 'Connection test failed' }));
      console.error('   ❌ Connection test failed:', errorData);
      throw new Error(errorData.message || 'Failed to test database connection');
    }

    const testResult = await testResponse.json();
    console.log('   ✅ Connection test successful');

    // If test succeeds, connect the database
    console.log('   - Connecting database...');
    const connectResponse = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}synctrack/database-connector/connect?connectorId=${encodeURIComponent(connectorId)}`,
      {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
      }
    );

    if (!connectResponse.ok) {
      const errorData = await connectResponse.json().catch(() => ({ message: 'Connection failed' }));
      console.error('   ❌ Connection failed:', errorData);
      throw new Error(errorData.message || 'Failed to connect database');
    }

    const connectResult = await connectResponse.json();
    console.log('   ✅ Database connected successfully');
    console.log('   - Connection result:', connectResult);

    // Return a token-like response (we don't actually get a token, but need to return something)
    return {
      connected: true,
      message: 'Database connected successfully',
      host: dbHost,
      port: dbPort,
      database: dbName
    };

  } catch (error) {
    console.error('   ❌ Error in PostgreSQL connection:', error);
    throw error;
  }
};

/**
 * Test PostgreSQL database connection with provided credentials.
 * This is a standalone function that can be called from the UI.
 * 
 * @param {Object} credentials - Database connection credentials
 * @param {string} credentials.host - Database host
 * @param {string} credentials.port - Database port (optional, defaults to 5432)
 * @param {string} credentials.databaseName - Database name
 * @param {string} credentials.username - Database username
 * @param {string} credentials.password - Database password (plain text)
 * @returns {Promise<Object>} Test result with success status and message
 */
export const testPostgreSQLConnection = async (credentials) => {
  console.log('🔍 Frontend: testPostgreSQLConnection() called');
  console.log('   - Host:', credentials.host);
  console.log('   - Port:', credentials.port || '5432');
  console.log('   - Database:', credentials.databaseName);
  console.log('   - Username:', credentials.username);

  try {
    const { host, port = '5432', databaseName, username, password } = credentials;

    // Validate required fields
    if (!host || !databaseName || !username || !password) {
      throw new Error('Missing required fields: host, databaseName, username, password');
    }

    // Call test endpoint
    const testResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/database-connector/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        host,
        port,
        databaseName,
        username,
        password
      })
    });

    const testResult = await testResponse.json();

    if (!testResponse.ok) {
      console.error('   ❌ Connection test failed:', testResult);
      return {
        success: false,
        message: testResult.message || 'Connection test failed. Please check your credentials.'
      };
    }

    console.log('   ✅ Connection test successful');
    return {
      success: true,
      message: testResult.message || 'Connection test successful'
    };

  } catch (error) {
    console.error('   ❌ Error testing PostgreSQL connection:', error);
    return {
      success: false,
      message: error.message || 'Error testing connection'
    };
  }
};

export const disconnectPostgreSQLDatabase = async (connectorId) => {
  console.log('🔍 Frontend: disconnectPostgreSQLDatabase() called for connectorId:', connectorId);

  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/database-connector/disconnect`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });

    console.log('   - Disconnect response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Disconnection failed' }));
      console.error('   ❌ Disconnect failed:', errorData);
      throw new Error(errorData.message || 'Failed to disconnect database');
    }

    const result = await response.json();
    console.log('   ✅ Database disconnected successfully');
    console.log('   - Result:', result);

    return result;

  } catch (error) {
    console.error('   ❌ Error disconnecting PostgreSQL:', error);
    throw error;
  }
};

